import { useState, useMemo } from 'react';
import { Package, History, Plus, Trash2, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  WarehouseItem,
  CATEGORY_LABELS,
  useDeleteWarehouseItem,
  ProductCategory,
} from '@/hooks/warehouse';
import LowStockBadge from './LowStockBadge';
import RegisterMovementModal from './RegisterMovementModal';
import MovementHistoryModal from './MovementHistoryModal';
import EditWarehouseItemModal from './EditWarehouseItemModal';
import { getThumbnailImageUrl } from '@/lib/cloudinary';
import useRolePermissions from '@/hooks/admin/shared/useRolePermissions';

interface WarehouseTableProps {
  items: WarehouseItem[];
  isLoading: boolean;
  showOnlyLowStock?: boolean;
  searchTerm?: string;
  organizationId?: string;
  category?: string;
}

const WarehouseTable = ({
  items,
  isLoading,
  showOnlyLowStock = false,
  searchTerm = '',
  organizationId = '',
  category = '',
}: WarehouseTableProps) => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [movementItem, setMovementItem] = useState<WarehouseItem | null>(null);
  const [historyItem, setHistoryItem] = useState<WarehouseItem | null>(null);
  const [editingItem, setEditingItem] = useState<WarehouseItem | null>(null);

  const { role } = useRolePermissions();
  const isSuperAdmin = role === 'super_admin';

  const { mutate: deleteItem } = useDeleteWarehouseItem();

  const handleDelete = () => {
    if (deleteId) {
      deleteItem(deleteId);
      setDeleteId(null);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value);
  };

  // Filter items based on all criteria
  const filteredItems = useMemo(() => {
    let result = items;

    // Low stock filter
    if (showOnlyLowStock) {
      result = result.filter(
        (item) => item.min_stock_alert !== null && item.quantity <= item.min_stock_alert
      );
    }

    // Search filter (by product name)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((item) =>
        item.product?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Organization filter
    if (organizationId && organizationId !== 'all') {
      result = result.filter((item) => item.organization_id === organizationId);
    }

    // Category filter
    if (category && category !== 'all') {
      result = result.filter((item) => item.product?.category === category);
    }

    return result;
  }, [items, showOnlyLowStock, searchTerm, organizationId, category]);

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Cargando almacén...</div>;
  }

  if (filteredItems.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {showOnlyLowStock
          ? 'No hay productos con stock bajo'
          : searchTerm || organizationId || category
          ? 'No se encontraron productos con los filtros aplicados'
          : 'No hay productos en el almacén'}
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">Imagen</TableHead>
              <TableHead>Producto</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead className="text-right">Precio Unitario</TableHead>
              <TableHead className="text-center">Cantidad</TableHead>
              <TableHead className="text-center">Stock Mín.</TableHead>
              <TableHead>Organización</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.map((item) => (
              <TableRow key={item.id}>
                {/* Image Column */}
                <TableCell>
                  {item.product?.image_url ? (
                    <img
                      src={getThumbnailImageUrl(item.product.image_url)}
                      alt={item.product?.name || 'Producto'}
                      className="h-10 w-10 rounded object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-medium">
                    {item.product?.name || 'Producto desconocido'}
                  </div>
                  {item.product?.description && (
                    <div className="text-sm text-muted-foreground truncate max-w-[250px]">
                      {item.product.description}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {item.product?.category && (
                    <Badge variant="secondary">
                      {CATEGORY_LABELS[item.product.category as ProductCategory]}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                <TableCell className="text-center">
                  <LowStockBadge quantity={item.quantity} minStockAlert={item.min_stock_alert} />
                </TableCell>
                <TableCell className="text-center text-muted-foreground">
                  {item.min_stock_alert !== null ? item.min_stock_alert : '-'}
                </TableCell>
                <TableCell>
                  {item.organization ? (
                    <Badge variant="outline">{item.organization.name}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {isSuperAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setMovementItem(item)}
                        title="Registrar movimiento"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setHistoryItem(item)}
                      title="Ver historial"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    {isSuperAdmin && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingItem(item)}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteId(item.id)}
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Register Movement Modal */}
      <RegisterMovementModal
        isOpen={!!movementItem}
        onClose={() => setMovementItem(null)}
        warehouseItem={movementItem}
      />

      {/* Movement History Modal */}
      <MovementHistoryModal
        isOpen={!!historyItem}
        onClose={() => setHistoryItem(null)}
        warehouseItem={historyItem}
      />

      {/* Edit Warehouse Item Modal */}
      <EditWarehouseItemModal
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        warehouseItem={editingItem}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el item del almacén y todo su
              historial de movimientos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WarehouseTable;
